import React from 'react';
import {Image, Page, Text, View} from '@react-pdf/renderer';
import {IntlShape} from 'react-intl';
import {companyLogoSrc} from '../../../../directApi';
import {stylesFor} from './quotePdfStyles';
import {footerContactLine} from '../quoteDocument';
import type {QuoteDocumentData, QuoteTemplate} from '../resolver/types';

type QuotePageProps = {
	data: QuoteDocumentData;
	template: QuoteTemplate;
	intl: IntlShape;
	children: React.ReactNode;
};

/**
 * The A4 frame every quote page shares: a fixed running header (small logo, "Client · Project",
 * "Confidential") and a fixed footer (company name and contact, "N of T" with real page numbers).
 * Either can be
 * turned off for the whole document, and the header never carries the quote reference — that stays
 * on the cover, where a reader looks for it once.
 */
const QuotePage = ({data, template, intl, children}: QuotePageProps): JSX.Element => {
	const styles = stylesFor(template);
	const logoSrc = template.branding.showLogo && data.companyLogoId ? companyLogoSrc(data.companyLogoId) : null;
	const runningHead = data.clientName ? `${data.clientName} · ${data.projectName}` : data.projectName;

	return (
		<Page size="A4" style={styles.page}>
			{template.layout.pageHeader && (
				<View fixed style={styles.band}>
					<View style={styles.bandRun}>
						{logoSrc ? (
							<Image src={{uri: logoSrc, credentials: 'include'}} style={styles.bandLogoImage} />
						) : (
							<Text style={styles.bandLogoWord}>{data.companyName}</Text>
						)}
						<Text style={styles.bandRunText}>{runningHead}</Text>
					</View>
					<Text style={styles.bandConfidential}>{intl.formatMessage({id: 'quote_designer.pdf.confidential'})}</Text>
				</View>
			)}
			<View style={styles.content}>{children}</View>
			{template.layout.pageFooter && (
				<View fixed style={styles.foot}>
					<Text style={styles.footText}>{footerContactLine(data.companyName, data.companyContactEmail)}</Text>
					<Text
						style={styles.footText}
						render={({pageNumber, totalPages}) =>
							intl.formatMessage({id: 'quote_designer.pdf.page_of'}, {page: pageNumber, total: totalPages})
						}
					/>
				</View>
			)}
		</Page>
	);
};

export default QuotePage;
